import { Input } from '@iwoca/orion';
import {
    Control,
    FieldErrors,
    useFieldArray,
    UseFormRegister
} from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { renderFieldError } from '../../../../forms/helpers/renderFieldError';
import { getCountry } from '../../../../iwIntlProvider/CountryProvider';
import { PlusSVG } from '../../../../svgComponents/plusSVG/index';
import { TrashcanSVG } from '../../../../svgComponents/trashcanSVG/index';
import styles from '../PersonModal.module.css';
import { TPersonFormValues } from '../PersonModal.types';

type ContactDetailsProps = {
    register: UseFormRegister<TPersonFormValues>;
    control: Control<TPersonFormValues>;
    errors: FieldErrors<TPersonFormValues>;
    isGuarantor: boolean;
    isApplicant?: boolean;
    isEditingPerson?: boolean;
    hasCreditDecision?: boolean;
};

const ContactDetails = ({
    register,
    control,
    errors,
    isGuarantor,
    isApplicant,
    isEditingPerson,
    hasCreditDecision
}: ContactDetailsProps) => {
    const intl = useIntl();
    const { fields, append, remove } = useFieldArray({
        control,
        name: 'phones'
    });

    const personalDetailsEditingDisabled =
        hasCreditDecision && isEditingPerson && isGuarantor && !isApplicant;

    const renderContactDetailsSection = () => {
        const emailDisabled =
            hasCreditDecision && isEditingPerson && isGuarantor;

        return (
            <>
                <div className={styles.subTitle}>
                    <FormattedMessage
                        id="_.person.contact_details"
                        defaultMessage="Contact details"
                    />
                </div>

                <div className="fs-exclude mb-xl">
                    {/* eslint-disable-next-line iwoca/input-a11y */}
                    <Input
                        {...register('email')}
                        label={intl.formatMessage({
                            id: '_.person.email',
                            defaultMessage: 'Email address'
                        })}
                        data-testid="email-input"
                        disabled={!!emailDisabled}
                        error={renderFieldError(errors.email)}
                    />
                </div>

                {renderPhoneFields()}
            </>
        );
    };

    const renderPhoneFields = () => {
        const phonesErrors = errors.phones as
            | Array<
                  { number?: { message?: unknown; type?: string } } | undefined
              >
            | undefined;

        return (
            <>
                {fields.map((field, index) => {
                    const phoneError = phonesErrors?.[index]?.number;

                    return (
                        <div
                            key={field.id}
                            className={`${styles.removeWrapper} mb-xl`}
                        >
                            {index === 0 ? (
                                <div className="fs-exclude">
                                    {/* eslint-disable-next-line iwoca/input-a11y */}
                                    <Input
                                        {...register(
                                            `phones.${index}.number` as const
                                        )}
                                        label={intl.formatMessage({
                                            id: '_.person.phone',
                                            defaultMessage: 'Phone number'
                                        })}
                                        data-testid="phone-input"
                                        disabled={
                                            !!personalDetailsEditingDisabled
                                        }
                                        error={renderFieldError(
                                            phoneError as any
                                        )}
                                    />
                                </div>
                            ) : (
                                <>
                                    <div className="fs-exclude">
                                        {/* eslint-disable-next-line iwoca/input-a11y */}
                                        <Input
                                            {...register(
                                                `phones.${index}.number` as const
                                            )}
                                            data-testid="phone-input"
                                            disabled={
                                                !!personalDetailsEditingDisabled
                                            }
                                            error={renderFieldError(
                                                phoneError as any
                                            )}
                                        />
                                    </div>
                                    {personalDetailsEditingDisabled ? null : (
                                        <div
                                            className={styles.removeBtn}
                                            onClick={() => remove(index)}
                                            data-testid="remove-phone-number"
                                        >
                                            <TrashcanSVG />
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    );
                })}
                {personalDetailsEditingDisabled ? null : (
                    <button
                        className={styles.addBtn}
                        type="button"
                        onClick={() => append({ number: '' })}
                    >
                        <PlusSVG className={styles.addIcon} />
                        <FormattedMessage
                            id="_.person.add_another_phone_number"
                            defaultMessage="Add another phone number"
                        />
                    </button>
                )}
            </>
        );
    };

    if (getCountry() === 'DE') {
        return renderContactDetailsSection();
    }

    return isGuarantor ? renderContactDetailsSection() : null;
};

// eslint-disable-next-line import/no-default-export
export default ContactDetails;
